import { useCallback } from 'react';

import { toCustomFieldArray } from '@givebacks/utils';
import { DateTime } from 'luxon';
import { IconBling, IconReceipt } from 'ui-icons';

import { ICON_API_MAP } from '../../../../Icons/config/icon-api-map';
import { CardCardCart } from './cartCard.types';
import type { CartItem } from './types';

type CartItemTypes =
  | 'donation'
  | 'campaignDonation'
  | 'athonPledge'
  | 'product'
  | 'membership'
  | 'extras'
  | 'ticket'
  | 'seasonPass'
  | 'pos'
  | 'studentFee'
  | '';

interface LocalCartItem extends Pick<CartItem, 'peer_pages' | 'uuid' | 'reporting_store_item'> {
  store_item: Pick<CartItem['store_item'], 'cause_id' | 'uuid'>;
}

const useGetCartItemType = (cart: CardCardCart) => {
  const getCartItemType = useCallback(
    (cartItem: Pick<CartItem, 'store_item' | 'status'>): CartItemTypes => {
      if (cartItem?.store_item?.type?.toLowerCase().includes('donation')) {
        return 'donation';
      }

      if (cartItem?.store_item?.type?.toLowerCase().includes('campaign')) {
        return 'campaignDonation';
      }

      if (
        cart.status !== 'pledge_required' &&
        cartItem?.store_item?.type?.toLowerCase().includes('athon')
      ) {
        return 'campaignDonation';
      }

      if (
        cart.status === 'pledge_required' &&
        cartItem?.store_item?.type?.toLowerCase().includes('athon')
      ) {
        return 'athonPledge';
      }

      if (cartItem?.store_item?.type?.toLowerCase().includes('membership')) {
        return 'membership';
      }

      if (cartItem?.store_item?.type?.toLowerCase().includes('benefit')) {
        return 'extras';
      }

      if (cartItem?.store_item?.type?.toLowerCase().includes('ticket')) {
        return 'ticket';
      }

      if (cartItem?.store_item?.type?.toLowerCase().includes('season')) {
        return 'seasonPass';
      }

      if (cartItem?.store_item?.type?.toLowerCase().includes('storeitems::item')) {
        return 'product';
      }

      if (cartItem?.store_item?.type?.toLowerCase().includes('pos')) {
        return 'pos';
      }

      if (cartItem?.store_item?.type?.toLowerCase().includes('studentfee')) {
        return 'studentFee';
      }

      return '';
    },
    [cart.status]
  );

  return getCartItemType;
};

const typeToTitleMap = new Map<CartItemTypes, (cartItem: CartItem) => string>([
  ['donation', () => 'Donation'],
  ['campaignDonation', () => 'Donation'],
  ['athonPledge', () => 'Pledge'],
  ['product', (cartItem: CartItem) => cartItem?.store_item?.name ?? 'Store Item'],
  ['membership', () => 'Membership'],
  ['extras', (cartItem: CartItem) => cartItem?.store_item?.name ?? 'Extra'],
  [
    'ticket',
    (cartItem: CartItem) =>
      `${cartItem?.store_item?.name ?? 'Ticket'}${
        cartItem?.event_date?.title ? `, ${cartItem.event_date.title}` : ''
      }`,
  ],
  [
    'seasonPass',
    (cartItem: CartItem) =>
      `${cartItem?.store_item?.name ?? 'Season Ticket'}${
        cartItem?.reporting_store_item?.name ? `, ${cartItem.reporting_store_item.name}` : ''
      }`,
  ],
  ['pos', () => 'Quick Pay'],
  ['studentFee', (cartItem: CartItem) => cartItem?.store_item?.name ?? 'Student Fee'],
  ['', () => 'Store Item'],
]);

const typeToDescriptionMap = new Map<CartItemTypes, (cartItem: CartItem) => string | string[]>([
  [
    'donation',
    (cartItem: CartItem) => {
      return cartItem.isRecurring ? 'Monthly' : 'One-time';
    },
  ],
  [
    'campaignDonation',
    (cartItem: CartItem) => {
      const parts: string[] = [];
      const peers: string[] = [];

      parts.push(cartItem?.store_item?.name ?? 'Store Item');

      if (cartItem?.designation) {
        parts.push(`${cartItem.designation}`);
      }

      if (cartItem?.peer_pages?.[0]) {
        cartItem?.peer_pages.map((peer) => {
          if ('name' in peer) {
            peers.push(peer.name);
          }
          if ('first_name' in peer && 'last_name' in peer) {
            peers.push(`${peer.first_name} ${peer.last_name}`);
          }
        });
      }

      if (cartItem?.credit_write_in) {
        cartItem.credit_write_in.map((writeIn) => {
          peers.push(writeIn);
        });
      }

      if (peers.length > 0) parts.push(peers.join(', '));

      return parts.join(' / ');
    },
  ],
  [
    'athonPledge',
    (cartItem: CartItem) => {
      const parts: string[] = [];

      parts.push(cartItem?.store_item?.name ?? 'Pledge');

      const creditToId = cartItem?.credit_to?.[0];

      if (creditToId && cartItem?.peer_pages?.[0]) {
        const creditedParticipant = cartItem.peer_pages.find((peer) => peer.uuid === creditToId);

        if (!creditedParticipant) {
          return parts.join(' / ');
        }

        if ('name' in creditedParticipant) {
          parts.push(creditedParticipant.name);
        }

        if ('first_name' in creditedParticipant && 'last_name' in creditedParticipant) {
          parts.push(`${creditedParticipant.first_name} ${creditedParticipant.last_name}`);
        }
      }

      return parts.join(' / ');
    },
  ],
  [
    'product',
    (cartItem: CartItem) => {
      const additionalFields = toCustomFieldArray(
        cartItem.options ?? {},
        cartItem.store_item?.options ?? []
      );

      return additionalFields.join(' / ');
    },
  ],
  [
    'membership',
    (cartItem: CartItem) => {
      const additionalFields = toCustomFieldArray(
        cartItem.options ?? {},
        cartItem.store_item?.options ?? []
      );

      return additionalFields.join(' / ');
    },
  ],
  [
    'extras',
    (cartItem: CartItem) => {
      const parts: string[] = [cartItem.store_item?.store_item?.name ?? ''];

      const additionalFields = toCustomFieldArray(
        cartItem.options ?? {},
        cartItem.store_item?.options ?? []
      );

      parts.push(...additionalFields);

      return parts.join(' / ');
    },
  ],
  [
    'ticket',
    (cartItem: CartItem) => {
      const parts: string[] = [];
      parts.push(cartItem?.reporting_store_item?.name ?? '');
      if (cartItem?.event_date?.starting_at) {
        const startDate = DateTime.fromISO(cartItem?.event_date?.starting_at).toFormat(
          'ccc, LLL d'
        );
        const startTime = DateTime.fromISO(cartItem?.event_date?.starting_at)
          .toLocaleString(DateTime.TIME_SIMPLE)
          .replace(' AM', 'am')
          .replace(' PM', 'pm');
        const timezone = DateTime.fromISO(cartItem?.event_date?.starting_at).toFormat('ZZZZ');

        parts.push(startDate);
        parts.push(`${startTime} ${timezone}`);
      }

      const additionalFields = toCustomFieldArray(
        cartItem.options ?? {},
        cartItem.store_item?.options ?? []
      );

      parts.push(...additionalFields);

      return parts.join(' / ');
    },
  ],
  [
    'seasonPass',
    (cartItem: CartItem) => {
      const additionalFields = toCustomFieldArray(
        cartItem.options ?? {},
        cartItem.store_item?.options ?? []
      );

      return additionalFields.join(' / ');
    },
  ],
  [
    'pos',
    (cartItem: CartItem) => {
      return cartItem?.notes ?? '';
    },
  ],
  [
    'studentFee',
    (cartItem: CartItem) => {
      return cartItem?.fulfillment?.student_fee?.student ?? 'Student Fee';
    },
  ],
  ['', () => 'Store Item'],
]);

interface BuildCauseUrl {
  (
    pathSegments: (string | undefined)[],
    cause?: { shortener?: string; uuid?: string },
    cartItem?: LocalCartItem
  ): string;
}

/**
 * Safely builds a cause URL path by filtering out undefined/null/empty segments
 * @param pathSegments - Array of path segments (e.g., ['shop', 'items', itemId])
 * @param cause - Optional cause object with shortener or uuid
 * @param cartItem - Optional cart item for fallback cause identification
 * @returns A valid URL path with /causes as fallback
 */
const buildCauseUrl: BuildCauseUrl = (pathSegments, cause, cartItem) => {
  // Try to get cause identifier: shortener > uuid > store_item?.cause_id
  const causeId = cause?.shortener ?? cause?.uuid ?? cartItem?.store_item?.cause_id;

  // Filter out undefined, null, or empty segments
  const validSegments = pathSegments.filter(
    (segment): segment is string => segment !== undefined && segment !== null && segment !== ''
  );

  // If no cause identifier, return fallback
  if (!causeId) {
    return '/causes';
  }

  // If no valid segments, return just the cause page
  if (validSegments.length === 0) {
    return `/causes/${causeId}`;
  }

  return `/causes/${causeId}/${validSegments.join('/')}`;
};

type TypeToLinkMap = (
  cartItem: LocalCartItem,
  cause?: { shortener?: string; uuid: string }
) => string;
/**
 * note: these links are only used within the context of the givebacks app.
 * Future usage of cart item links in other contexts will necessitate a refactor to handle other cases
 * e.g. NPO storefront
 */
const typeToLinkMap = new Map<CartItemTypes, TypeToLinkMap>([
  // go to cause page
  ['donation', (cartItem, cause) => buildCauseUrl([], cause, cartItem)],
  // could be either a campaign / athon donation -> go to campaign page
  [
    'campaignDonation',
    (cartItem, cause) => {
      // plain campaign donation -> go to campaign page
      if (!cartItem.peer_pages || cartItem.peer_pages.length === 0) {
        return buildCauseUrl(['campaigns', cartItem.reporting_store_item?.uuid], cause, cartItem);
      }

      // multi-participant campaign donation -> go to participant center
      if (cartItem.peer_pages.length > 1) {
        return buildCauseUrl(
          ['campaigns', cartItem.reporting_store_item?.uuid, 'participant-center'],
          cause,
          cartItem
        );
      }

      // single participant or team campaign donation -> go to participant or team page
      const peerId = cartItem.peer_pages[0]?.uuid;
      const isParticipant = cartItem.peer_pages.some((peer) =>
        peer?.type?.toLowerCase().includes('participant')
      );

      const peerType = isParticipant ? 'participants' : 'teams';

      return buildCauseUrl(
        ['campaigns', cartItem.reporting_store_item?.uuid, 'participant-center', peerType, peerId],
        cause,
        cartItem
      );
    },
  ],
  // go to product detail page
  [
    'product',
    (cartItem, cause) =>
      buildCauseUrl(['shop', 'items', cartItem.store_item?.uuid], cause, cartItem),
  ],
  // go to product detail page
  [
    'membership',
    (cartItem, cause) =>
      buildCauseUrl(['shop', 'items', cartItem.store_item?.uuid], cause, cartItem),
  ],
  // the extra could be added from either a fundraiser or ticketed event -> go to corresponding page
  [
    'extras',
    (cartItem, cause) => {
      const isCampaign = cartItem.reporting_store_item?.type?.toLowerCase().includes('campaign');
      const itemType = isCampaign ? 'campaigns' : 'events';

      return buildCauseUrl([itemType, cartItem.reporting_store_item?.uuid], cause, cartItem);
    },
  ],
  // go to the event page
  [
    'ticket',
    (cartItem, cause) =>
      buildCauseUrl(['events', cartItem.reporting_store_item?.uuid], cause, cartItem),
  ],
  // go to the event page
  [
    'seasonPass',
    (cartItem, cause) =>
      buildCauseUrl(['events', cartItem.reporting_store_item?.uuid], cause, cartItem),
  ],
  ['', () => '/causes'],
]);

interface GetCartItemIcon<T extends CartItemTypes> {
  (
    type: T,
    cartItem: CartItem
  ): T extends 'studentFee'
    ? (typeof ICON_API_MAP)[keyof typeof ICON_API_MAP]
    : T extends 'pos'
      ? typeof IconBling
      : undefined;
}

/** Returns an icon for the cart item if one exists for the store item type */
const getCartItemIcon: GetCartItemIcon<CartItemTypes> = (
  type: CartItemTypes,
  cartItem: CartItem
) => {
  if (type === 'studentFee') {
    const icon = cartItem?.fulfillment?.student_fee?.icon;
    if (icon && ICON_API_MAP[icon]) {
      return ICON_API_MAP[icon];
    } else {
      return IconReceipt;
    }
  }

  if (type === 'pos') {
    return IconBling;
  }

  return undefined;
};

export {
  buildCauseUrl,
  getCartItemIcon,
  typeToDescriptionMap,
  typeToLinkMap,
  typeToTitleMap,
  useGetCartItemType,
};
